<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="pa-4">
            <!-- Loading State -->
            <div v-if="loadingData" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="orange"
                size="64"
              ></v-progress-circular>
              <p class="mt-4 text-h6 text-grey">Loading approval details...</p>
            </div>

            <!-- Content (shown when not loading) -->
            <div v-else>
              <!-- Header Section -->
              <div class="mb-4">
                <h2 class="text-h5 font-weight-bold mb-2"> {{ $route.query.page_title  }} Approval Details</h2>
                
                <div class="text-body-2">
                  <div><strong> Code:</strong> {{ documentDetails?.code || 'N/A' }}</div>
                  <div><strong> Date:</strong> {{ formatDateTime(documentDetails?.created_at) }}</div>
                  
                </div>
              </div>

            <!-- Show message when all steps are completed -->
            <v-alert
              v-if="approval_process.length > 0 && isProcessComplete"
              type="success"
              icon="mdi-check-all"
              variant="tonal"
              class="mb-4"
              border="start"
              prominent
            >
              <div class="text-subtitle-2 font-weight-bold mb-1">
                ✓ All approval steps completed
              </div>
              <div class="text-body-2">
                <span v-if="wasForwardApproved">
                  This request has been fully approved. The approval process was completed early.
                </span>
                <span v-else>
                  This request has been fully processed.
                </span>
              </div>
            </v-alert>

            <!-- Approval Workflow Timeline -->
            <div class="mb-4">
              <div class="horizontal-timeline">
                <div 
                  v-for="(approval, index) in approval_process" 
                  :key="index"
                  class="timeline-step"
                >
                  <div 
                    v-if="index > 0" 
                    class="step-connector"
                    :class="{ 
                      'connector-approved': approval_process[index - 1]?.status?.toLowerCase() === 'approved' || 
                                           (isProcessComplete && (approval_process[index - 1]?.status?.toLowerCase() === 'approved' || approval_process[index - 1]?.status?.toLowerCase() === 'pending'))
                    }"
                  >
                    <div class="connector-arrow"></div>
                  </div>
                  <div class="step-content">
                    <div 
                      class="step-circle" 
                      :class="getApprovalClass(approval.status, index)"
                    >
                      <v-icon size="medium" color="white">
                        {{ getApprovalIcon(approval.status, index) }}
                      </v-icon>
                    </div>
                    <div class="step-info">
                      <div class="step-title status-approved-text" v-if="approval.status === 'Approved'">
                        Approved By {{ approval.approval_step_name?.name || 'Approver' }}
                      </div>
                      <div class="step-title status-cancelled-text" v-else-if="approval.status === 'Cancelled'">
                        Cancelled By {{ approval.approval_step_name?.name || 'Approver' }}
                      </div>
                      <div class="step-title status-skipped-text" v-else-if="approval.status === 'Pending' && isProcessComplete">
                        Skipped - {{ approval.approval_step_name?.name || 'Approver' }}
                      </div>
                      <div class="step-title" v-else-if="approval.status === 'Pending' && !isProcessComplete && isCurrentStep(index)">
                        <span class="current-step-title">Waiting for {{ approval.approval_step_name?.name || 'Approver' }}</span>
                      </div>
                      <div class="step-title status-pending-text" v-else-if="approval.status === 'Pending' && !isProcessComplete">
                        Waiting for {{ approval.approval_step_name?.name || 'Approver' }}
                      </div>
                      <div class="step-title" v-else>
                        {{ approval.approval_step_name?.name || 'Approver' }}
                      </div>
                      <div class="step-status">
                        <span v-if="approval.status === 'Approved'">
                          {{ formatDateTime(approval.approved_at) }}
                        </span>
                        <span v-else-if="approval.status === 'Cancelled'">
                          {{ formatDateTime(approval.approved_at) }}
                        </span>
                        <span v-else-if="approval.status === 'Pending' && isProcessComplete">
                          <div class="text-grey" style="font-style: italic;">
                            Process completed early
                          </div>
                        </span>
                        <span v-else-if="approval.status === 'Pending' && !isProcessComplete">
                          <div v-if="approval.employee?.name" class="employee-name">
                            {{ approval.employee.name }}
                          </div>
                          <div v-if="isCurrentStep(index)">
                            Started: {{ formatDateTime(getCurrentStepStartTime(index)) }}
                          </div>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Previous Comments Section -->
            <div class="mb-4">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">Previous Comments:</h3>
              <div v-if="previousComments.length > 0">
                <div 
                  v-for="(comment, index) in previousComments" 
                  :key="index"
                  class="comment-box mb-2"
                  :class="{
                    'comment-box-rejected': comment.status === 'Cancelled',
                    'comment-box-approved': comment.status === 'Approved'
                  }"
                >
                  <div class="comment-header">
                    <span class="comment-status" :class="{
                      'status-rejected': comment.status === 'Cancelled',
                      'status-approved': comment.status === 'Approved'
                    }">
                      {{ comment.status }}:
                    </span>
                    {{ comment.approval_step_name?.name }}: {{ comment.comments || 'No comments' }}
                  </div>
                  <div class="comment-date">
                    {{ formatDateTime(comment.approved_at) }}
                  </div>
                </div>
              </div>
              <div v-else class="text-grey text-body-2 pa-3" style="background: #f5f5f5; border-radius: 4px;">
                No previous comments yet
              </div>
            </div>

            <!-- Add Your Comment Section -->
            <div class="mb-4" v-if="currentApprovalStep && canApprove">
              <h3 class="text-subtitle-1 font-weight-bold mb-2">Add Your Comment:</h3>
              <v-textarea
                v-model="userComment"
                placeholder="Write your comments here (optional)..."
                rows="3"
                variant="outlined"
                bg-color="grey-lighten-5"
                density="compact"
                maxlength="225"
                :counter="225"
                :error-messages="commentError"
                clearable
              ></v-textarea>
            </div>

            <!-- Action Buttons -->
            <div class="d-flex gap-2" v-if="canApprove || canReject">
              <v-btn
                v-if="canApprove && isForwardEnabled"
                color="yellow"
                @click="handleForward"
                :loading="loadingForward"
                :disabled="loadingApprove || loadingReject"
              >
                Forward
              </v-btn>
              <v-btn
                v-if="canApprove"
                color="success"
                @click="handleApprove"
                :loading="loadingApprove"
                :disabled="loadingReject || loadingForward"
              >
                Approve
              </v-btn>
              <v-btn
                v-if="canReject"
                color="error"
                @click="handleCancel"
                :loading="loadingReject"
                :disabled="loadingApprove || loadingForward"
              >
                Reject
              </v-btn>
              <!-- <v-btn
                color="grey"
                @click="goBack"
              >
                Back
              </v-btn> -->
            </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
</template>

<script>
import Axios from "axios";
import { toast } from "vue3-toastify";
import { 
  getApprovalRequestDetails as getApprovalRequestDetailsApi,
  updateApprovalStatus as updateApprovalStatusApi
} from "@/store/api";

export default {
  name: "JournalVoucherApprovalPreview",

  data() {
    return {
      approval_process: [],
      documentDetails: null,
      userComment: "",
      commentError: "",
      // Separate loading flags so only the clicked action shows loading
      loadingApprove: false,
      loadingReject: false,
      loadingForward: false,
      loadingData: true,
    };
  },

  computed: {
    previousComments() {
      return this.approval_process.filter(
        (approval) => approval.status === 'Approved' || approval.status === 'Cancelled'
      ).filter(approval => approval.comments); // Only show if there are comments
    },

    // Check if user has approve permission using approval_permission from API
    canApprove() {
      const hasPermission = this.currentApprovalStep?.approval_permission === true;
      console.log('Checking canApprove:', {
        currentStep: this.currentApprovalStep,
        approval_permission: this.currentApprovalStep?.approval_permission,
        hasPermission: hasPermission
      });
      return this.currentApprovalStep && hasPermission;
    },

    // Check if user has reject permission using approval_permission from API
    canReject() {
      const hasPermission = this.currentApprovalStep?.approval_permission === true;
      console.log('Checking canReject:', {
        currentStep: this.currentApprovalStep,
        approval_permission: this.currentApprovalStep?.approval_permission,
        hasPermission: hasPermission
      });
      return this.currentApprovalStep && hasPermission;
    },

    currentApprovalStep() {
      // Check if the parent document is already approved (process ended)
      // This happens when is_forward = 1 and Approve was clicked
      const parentApprovalStatus = this.documentDetails?.approval_status;
      if (parentApprovalStatus?.toLowerCase() === 'approved') {
        console.log('Parent document is already approved - process ended');
        return null; // No current step if already approved
      }

      // Find the first pending approval step
      const currentStep = this.approval_process.find(
        (approval) => approval.status?.toLowerCase() === 'pending'
      );

      if (!currentStep) return null;

      // The approval_permission is already provided by the API
      // No need to calculate it manually
      const isForward = currentStep?.matrix_detail?.matrix?.approval_name?.is_forward;
      console.log('Current approval step:', {
        id: currentStep.id,
        employee: currentStep.employee?.name,
        approval_step_name: currentStep.approval_step_name?.name,
        approval_permission: currentStep.approval_permission,
        status: currentStep.status,
        is_forward: isForward,
        is_final_step: currentStep.is_final_step,
        matrix_detail: currentStep.matrix_detail,
        parent_approval_status: parentApprovalStatus
      });
      
      return currentStep;
    },

    isFinalStep() {
      // Use the is_final_step field directly from the API response
      return this.currentApprovalStep?.is_final_step === 1;
    },

    // Check if forward is enabled for this approval name
    isForwardEnabled() {
      // Check is_forward from the nested structure: matrix_detail.matrix.approval_name.is_forward
      const isForward = this.currentApprovalStep?.matrix_detail?.matrix?.approval_name?.is_forward;
      const enabled = isForward === 1 || isForward === '1';
      console.log('isForwardEnabled check:', {
        isForward: isForward,
        enabled: enabled,
        currentStep: this.currentApprovalStep?.id,
        approval_name: this.currentApprovalStep?.matrix_detail?.matrix?.approval_name
      });
      return enabled;
    },

    // Check if the approval process is complete
    isProcessComplete() {
      // Check from documentDetails first
      let parentApprovalStatus = this.documentDetails?.approval_status;
      
      // If not found, try to get it from the first approval step's referenceable
      if (!parentApprovalStatus && this.approval_process.length > 0) {
        parentApprovalStatus = this.approval_process[0]?.referenceable?.approval_status;
      }
      
      const isComplete = parentApprovalStatus?.toLowerCase() === 'approved';
      console.log('isProcessComplete check:', {
        documentDetails: this.documentDetails,
        approval_status: parentApprovalStatus,
        isComplete: isComplete,
        firstStepReferenceable: this.approval_process[0]?.referenceable
      });
      return isComplete;
    },

    // Check if the process was completed via forward approval (is_forward = 1)
    wasForwardApproved() {
      if (!this.isProcessComplete) return false;
      
      // Check if any approved step has is_forward = 1
      const approvedStep = this.approval_process.find(
        (approval) => approval.status?.toLowerCase() === 'approved'
      );
      
      if (approvedStep) {
        const isForward = approvedStep?.matrix_detail?.matrix?.approval_name?.is_forward;
        return isForward === 1 || isForward === '1';
      }
      
      return false;
    },
  },

  watch: {
    // Clear error message when user starts typing
    userComment() {
      if (this.commentError && this.userComment) {
        this.commentError = '';
      }
    }
  },

  created() {
    console.log('Approval Preview Component Created');
    console.log('Voucher ID from route:', this.$route.params.id);
    this.loadApprovalDetails();
  },

  methods: {
    async loadApprovalDetails() {
      try {
        this.loadingData = true;
        
        if (!this.$route.params.id && !this.$route.query.referenceable_id) {
          toast.error("No voucher ID provided");
          this.goBack();
          return;
        }

        // Get parameters from route query
        const referenceableType = this.$route.query.referenceable_type;
        const referenceableId = this.$route.query.referenceable_id || this.$route.params.id;
        
        // Check if referenceable_type is provided
        if (!referenceableType) {
          toast.error("Missing reference type. Unable to load approval details.");
          this.goBack();
          return;
        }
        
        console.log('Loading approval details...');
        console.log('Referenceable Type:', referenceableType);
        console.log('Referenceable ID:', referenceableId);
        
        const payload = {
          referenceable_type: referenceableType,
          referenceable_id: referenceableId,
        };
        
        const response = await Axios.post(getApprovalRequestDetailsApi, payload);

        if (response.data.success) {
          console.log('API Response:', response.data);
          this.approval_process = response.data.data || [];
          console.log('Current approval process:', this.approval_process);
          
          // Log the current step if it exists
          const currentStep = this.approval_process.find(step => step.status?.toLowerCase() === 'pending');
          console.log('Current step details:', currentStep);

          if (this.approval_process.length > 0) {
            this.documentDetails = this.approval_process[0].referenceable;
            
            // Log the parent approval status for debugging
            console.log('Parent document approval_status:', this.documentDetails?.approval_status);
            
            // If parent is already approved, log it
            if (this.documentDetails?.approval_status?.toLowerCase() === 'approved') {
              console.log('Process is complete - parent document is approved');
            }
          } else {
            toast.warning("No approval steps found for this document");
          }
        } else {
          toast.error(response.data.message || "Failed to load approval details");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load approval details");
        console.error('Error loading approval details:', error);
        this.goBack();
      } finally {
        this.loadingData = false;
      }
    },

    async handleForward() {
      if (!this.canApprove) {
        toast.error("You are not authorized to forward this step");
        return;
      }

      // Validate comment length only if comment is provided (defensive - textarea has maxlength)
      if (this.userComment && this.userComment.length > 225) {
        this.commentError = 'Comment cannot exceed 225 characters';
        toast.error('Comment cannot exceed 225 characters');
        return;
      }

      // Clear any previous errors
      this.commentError = '';

      this.loadingForward = true;
      try {
        const formData = new FormData();
        formData.append('comments', this.userComment || '');
        formData.append('status', 'Forwarded');

        const response = await Axios.post(
          `${updateApprovalStatusApi}/${this.currentApprovalStep.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          toast.success('Forwarded successfully');
          // Clear only the comment text after successful forward
          this.userComment = '';
          this.commentError = '';
          await this.loadApprovalDetails(); // Reload to show updated status
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to forward');
        console.error('Error details:', error.response || error);
      } finally {
        this.loadingForward = false;
      }
    },

    async handleApprove() {
      if (!this.canApprove) {
        toast.error("You are not authorized to approve this step");
        return;
      }

      // Validate comment length only if comment is provided (defensive - textarea has maxlength)
      if (this.userComment && this.userComment.length > 225) {
        this.commentError = 'Comment cannot exceed 225 characters';
        toast.error('Comment cannot exceed 225 characters');
        return;
      }

      // Clear any previous errors
      this.commentError = '';

      this.loadingApprove = true;
      try {
        const formData = new FormData();
        formData.append('comments', this.userComment || '');
        formData.append('status', 'Approved');

        const response = await Axios.post(
          `${updateApprovalStatusApi}/${this.currentApprovalStep.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          toast.success('Approved successfully');
          // Clear only the comment text after successful approval
          this.userComment = '';
          this.commentError = '';     
          await this.loadApprovalDetails(); // Reload to show updated status
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to approve');
        console.error('Error details:', error.response || error);
      } finally {
        this.loadingApprove = false;
      }
    },

    async handleCancel() {
      if (!this.canReject) {
        toast.error("You are not authorized to cancel this step");
        return;
      }

      // Validate comment length only if comment is provided (defensive - textarea has maxlength)
      if (this.userComment && this.userComment.length > 225) {
        this.commentError = 'Comment cannot exceed 225 characters';
        toast.error('Comment cannot exceed 225 characters');
        return;
      }

      // Clear any previous errors
      this.commentError = '';

      this.loadingReject = true;
      try {
        const formData = new FormData();
        formData.append('comments', this.userComment || '');
        formData.append('status', 'Cancelled');

        const response = await Axios.post(
          `${updateApprovalStatusApi}/${this.currentApprovalStep.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.success) {
          toast.success('Cancelled successfully');
          // Clear only the comment text after successful cancel
          this.userComment = '';
          this.commentError = '';
          await this.loadApprovalDetails(); // Reload to show updated status
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to cancel');
        console.error('Error details:', error.response || error);
      } finally {
        this.loadingReject = false;
      }
    },

    getDateField() {
      if (!this.documentDetails) return 'N/A';
      
      // Try common date field names
      return this.documentDetails.voucher_date || 
             this.documentDetails.requisition_date || 
             this.documentDetails.order_date || 
             this.documentDetails.date || 
             this.documentDetails.created_at || 
             'N/A';
    },

    getAmountField() {
      if (!this.documentDetails) return 'N/A';
      
      // Try common amount field names
      const amount = this.documentDetails.total_debit ?? 
                     this.documentDetails.total_amount ?? 
                     this.documentDetails.amount ?? 
                     this.documentDetails.total_credit ??
                     this.documentDetails.grand_total ??
                     this.documentDetails.net_amount ??
                     this.documentDetails.total;
      
      return amount !== null && amount !== undefined ? this.formatCurrency(amount) : 'N/A';
    },

    goBack() {
      this.$router.push({ name: "approval-request-record" });
    },

    isCurrentStep(index) {
      // Don't show current step if process is complete
      if (this.isProcessComplete) return false;
      
      if (!this.approval_process || this.approval_process.length === 0) return false;

      const firstPendingIndex = this.approval_process.findIndex(
        (approval) => approval.status?.toLowerCase() === 'pending'
      );

      return index === firstPendingIndex;
    },

    getCurrentStepStartTime(index) {
      // If there's a previous step and it's approved, use its approval time
      if (index > 0 && this.approval_process[index - 1]) {
        const previousStep = this.approval_process[index - 1];
        if (previousStep.status?.toLowerCase() === 'approved' && previousStep.approved_at) {
          return previousStep.approved_at;
        }
      }
      // Otherwise, use the current step's created_at
      return this.approval_process[index]?.created_at;
    },

    getApprovalClass(status, index) {
      if (!status) return 'step-circle-grey';
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved') return 'step-circle-success';
      if (statusLower === 'pending') {
        // If process is complete and step is pending, show as skipped
        if (this.isProcessComplete) return 'step-circle-skipped';
        return this.isCurrentStep(index) ? 'step-circle-blue' : 'step-circle-pending';
      }
      if (statusLower === 'rejected' || statusLower === 'cancelled') return 'step-circle-error';
      return 'step-circle-grey';
    },

    getApprovalIcon(status, index) {
      if (!status) return 'mdi-help';
      const statusLower = status.toLowerCase();
      if (statusLower === 'approved') return 'mdi-check';
      if (statusLower === 'pending') {
        // If process is complete and step is pending, show as skipped
        if (this.isProcessComplete) return 'mdi-skip-next';
        return this.isCurrentStep(index) ? 'mdi-reload' : 'mdi-clock-outline';
      }
      if (statusLower === 'rejected' || statusLower === 'cancelled') return 'mdi-close';
      return 'mdi-help';
    },

    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },

    formatDateTime(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    },
  },
};
</script>

<style scoped>
/* Timeline Styles */
.horizontal-timeline {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0;
  padding: 20px 15px;
  border-radius: 8px;
  margin-bottom: 16px;
}

.timeline-step {
  display: flex;
  align-items: flex-start;
  position: relative;
  min-width: 180px;
  flex-shrink: 0;
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #e0e0e0;
  position: absolute;
  top: 24px;
  left: -30px;
  z-index: 0;
  transition: all 0.3s ease;
}

.connector-approved {
  background: #4CAF50;
}

.connector-arrow {
  position: absolute;
  right: -5px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid #e0e0e0;
  border-top: 4px solid transparent;
  border-bottom: 4px solid transparent;
}

.connector-approved .connector-arrow {
  border-left-color: #4CAF50;
}

.step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

.step-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1;
  position: relative;
  border: 2px solid white;
}

.step-circle-success {
  background: linear-gradient(135deg, #4CAF50 0%, #2e7d32 100%);
}

.step-circle-pending {
  background: linear-gradient(135deg, #FF9800 0%, #ef6c00 100%);
}

.step-circle-blue {
  background: linear-gradient(135deg, #2196F3 0%, #1565C0 100%);
  animation: pulse-ring 2s infinite;
}

.step-circle-error {
  background: linear-gradient(135deg, #f44336 0%, #c62828 100%);
}

.step-circle-grey {
  background: linear-gradient(135deg, #9e9e9e 0%, #616161 100%);
}

.step-circle-skipped {
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 100%);
  opacity: 0.6;
}

@keyframes pulse-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.7), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(33, 150, 243, 0), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(33, 150, 243, 0), 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.step-info {
  text-align: center;
}

.step-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #1976d2;
  margin-bottom: 2px;
}

.status-approved-text {
  color: #4caf50 !important;
}

.status-pending-text {
  color: #ef6c00 !important;
}

.status-cancelled-text {
  color: #f44336 !important;
}

.status-skipped-text {
  color: #757575 !important;
  font-style: italic;
}

.step-status {
  font-size: 0.75rem;
  color: #666;
}

.employee-name {
  font-size: 0.75rem;
  color: #424242;
  font-weight: 600;
  margin-bottom: 2px;
}

.current-step-title {
  color: #2196F3 !important;
}

/* Comment Box Styles */
.comment-box {
  background: #f5f5f5;
  border-left: 3px solid #2196F3;
  padding: 8px 12px;
  border-radius: 4px;
  word-break: break-word;
  transition: all 0.3s ease;
}

.comment-box-rejected {
  background: #fff5f5;
  border-left-color: #f44336;
}

.comment-box-approved {
  background: #f5fff5;
  border-left-color: #4caf50;
}

.comment-header {
  font-weight: 600;
  font-size: 0.875rem;
  color: #333;
  margin-bottom: 4px;
  line-height: 1.4;
}

.comment-status {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 4px;
  font-size: 0.75rem;
  font-weight: bold;
}

.status-rejected {
  background-color: #ffebee;
  color: #d32f2f;
}

.status-approved {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.comment-date {
  font-size: 0.75rem;
  color: #666;
  margin-top: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>

